import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';

// Format helpers (same as CreateFOCustomerForm)
const formatCustomerID = (value) => (value || '').replace(/[a-zA-Z]/g, (c) => c.toUpperCase());
const formatName = (value) => (value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
const formatEmail = (value) => (value || '').toLowerCase().trim();
// Remove all spaces, then: if exactly 8 digits prepend +65, else return as-is (e.g. +1222333444)
const formatPhone = (value) => {
    const v = (value || '').replace(/\s/g, '').trim();
    if (v.length === 8 && /^\d{8}$/.test(v)) return `+65${v}`;
    return v;
};
const formatTgNumber = (value) => {
    const v = (value || '').replace(/\s/g, '').trim();
    if (v.length === 8 && /^\d{8}$/.test(v)) return `+65${v}`;
    return v;
};
const formatAddress = (value) => {
    let v = (value || '').trim();
    v = v.replace(/,\s*,\s*/g, ', ');
    v = v
        .split(/\s+/)
        .map((token) => {
            if (token.toLowerCase() === 'sg') return 'SG';
            return token ? token.charAt(0).toUpperCase() + token.slice(1).toLowerCase() : '';
        })
        .join(' ')
        .replace(/\s+/g, ' ');
    v = v.replace(/\s*,\s*SG\s*$/i, ' SG');
    return v.trim();
};

const EditFOCustomerModal = ({ customer, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        customerID: '',
        Name: '',
        email: [''],
        Address: [''],
        Phone: [''],
        Whatsapp: [''],
        Telegram: [{ username: '', tgNumber: '' }],
        Height: '',
        Weight: '',
        waist: '',
        wrist: '',
        shoeSize: '',
        ringSize: '',
        remarks: '',
        createMMYY: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (customer) {
            // Format createMMYY for month input (YYYY-MM format)
            let formattedMMYY = '';
            if (customer.createMMYY) {
                const date = new Date(customer.createMMYY);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                formattedMMYY = `${year}-${month}`;
            }
            
            setFormData({
                customerID: customer.customerID || '',
                Name: customer.Name || '',
                email: customer.email && customer.email.length > 0 ? customer.email : [''],
                Address: customer.Address && customer.Address.length > 0 ? customer.Address : [''],
                Phone: customer.Phone && customer.Phone.length > 0 ? customer.Phone : [''],
                Whatsapp: customer.Whatsapp && customer.Whatsapp.length > 0 ? customer.Whatsapp : [''],
                Telegram: (customer.Telegram && customer.Telegram.length > 0
                    ? customer.Telegram.map(t => typeof t === 'string'
                        ? { username: t, tgNumber: '' }
                        : { username: t?.username ?? '', tgNumber: t?.tgNumber ?? '' })
                    : [{ username: '', tgNumber: '' }]),
                Height: customer.Height || '',
                Weight: customer.Weight || '',
                waist: customer.waist || '',
                wrist: customer.wrist || '',
                shoeSize: customer.shoeSize || '',
                ringSize: customer.ringSize || '',
                remarks: customer.remarks || '',
                createMMYY: formattedMMYY || ''
            });
        }
    }, [customer]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError('');

            // Clean array fields - remove empty strings and apply format on save
            const cleanedData = {
                ...formData,
                customerID: formatCustomerID(formData.customerID),
                Name: formatName(formData.Name),
                email: formData.email.filter(e => e && e.trim() !== '').map(formatEmail),
                Address: formData.Address.filter(a => a && a.trim() !== '').map(formatAddress),
                Phone: formData.Phone.filter(p => p && p.trim() !== '').map((p) => formatPhone(p.trim())),
                Whatsapp: formData.Whatsapp.filter(w => w && w.trim() !== '').map((w) => formatPhone(w.trim())),
                Telegram: formData.Telegram
                    .filter(t => (t?.username && String(t.username).trim()) || (t?.tgNumber && String(t.tgNumber).trim()))
                    .map(t => ({
                        username: (t.username && String(t.username).trim()) || undefined,
                        tgNumber: (t.tgNumber && String(t.tgNumber).trim()) ? formatTgNumber(String(t.tgNumber).trim()) : undefined
                    })),
                Height: formData.Height ? parseFloat(formData.Height) : undefined,
                Weight: formData.Weight ? parseFloat(formData.Weight) : undefined,
                waist: formData.waist ? parseFloat(formData.waist) : undefined,
                wrist: formData.wrist ? parseFloat(formData.wrist) : undefined,
                shoeSize: formData.shoeSize ? parseFloat(formData.shoeSize) : undefined,
                ringSize: formData.ringSize ? parseFloat(formData.ringSize) : undefined,
                createMMYY: formData.createMMYY ? new Date(formData.createMMYY) : undefined
            };

            const token = localStorage.getItem('token');
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/focustomers/${customer._id}`,
                cleanedData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );

            if (response.data.success) {
                onSuccess(response.data.data);
            }
        } catch (err) {
            setError(`Failed to update GZ customer: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const addArrayField = (fieldName) => {
        const defaultItem = fieldName === 'Telegram' ? { username: '', tgNumber: '' } : '';
        setFormData({
            ...formData,
            [fieldName]: [...formData[fieldName], defaultItem]
        });
    };

    const removeArrayField = (fieldName, index) => {
        const newArray = formData[fieldName].filter((_, i) => i !== index);
        const fallback = fieldName === 'Telegram' ? [{ username: '', tgNumber: '' }] : [''];
        setFormData({
            ...formData,
            [fieldName]: newArray.length > 0 ? newArray : fallback
        });
    };

    const updateArrayField = (fieldName, index, value) => {
        const newArray = [...formData[fieldName]];
        newArray[index] = value;
        setFormData({
            ...formData,
            [fieldName]: newArray
        });
    };

    const updateTelegramField = (index, field, value) => {
        const newTelegram = [...formData.Telegram];
        newTelegram[index] = { ...(newTelegram[index] || {}), [field]: value };
        setFormData({ ...formData, Telegram: newTelegram });
    };

    if (!customer) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-5xl my-8 min-w-0 overflow-x-hidden">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Edit GZ Customer</h2>
                    <button
                        onClick={onCancel}
                        className="text-gray-500 hover:text-gray-700"
                        aria-label="Close modal"
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                        {/* Row 1: Customer ID, Name (larger), Create MM/YY (smaller) – one row */}
                        <div className="grid gap-2" style={{ gridTemplateColumns: '2fr 2fr 1fr' }}>
                            <div className="min-w-0">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Customer ID *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.customerID}
                                    onChange={(e) => setFormData({ ...formData, customerID: formatCustomerID(e.target.value) })}
                                    className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div className="min-w-0">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.Name}
                                    onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
                                    onBlur={(e) => setFormData((prev) => ({ ...prev, Name: formatName(prev.Name) }))}
                                    className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div className="min-w-0">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Create MM/YY
                                </label>
                                <input
                                    type="month"
                                    value={formData.createMMYY}
                                    onChange={(e) => setFormData({ ...formData, createMMYY: e.target.value })}
                                    className="w-full min-w-0 px-2 py-2 border border-gray-300 rounded-md text-sm"
                                />
                            </div>
                        </div>

                        {/* Row 2: Email, Phone, WhatsApp, Telegram – one row, compact */}
                        <div className="grid gap-2 min-w-0" style={{ gridTemplateColumns: '1fr 1fr 1fr 2fr' }}>
                            {/* Email */}
                            <div className="min-w-0 overflow-hidden">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email
                                </label>
                                {formData.email.map((email, index) => (
                                    <div key={index} className="flex gap-1 mb-2 min-w-0">
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => updateArrayField('email', index, formatEmail(e.target.value))}
                                            className="flex-1 min-w-0 px-2 py-2 border border-gray-300 rounded-md text-sm"
                                            placeholder="email@example.com"
                                        />
                                        {formData.email.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeArrayField('email', index)}
                                                className="px-2 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addArrayField('email')}
                                    className="mt-1 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                >
                                    + Add Email
                                </button>
                            </div>

                            {/* Phone */}
                            <div className="min-w-0 overflow-hidden">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone
                                </label>
                                {formData.Phone.map((phone, index) => (
                                    <div key={index} className="flex gap-1 mb-2 min-w-0">
                                        <input
                                            type="tel"
                                            value={phone}
                                            onChange={(e) => updateArrayField('Phone', index, e.target.value)}
                                            onBlur={(e) => {
                                                const v = e.target.value;
                                                const formatted = formatPhone(v);
                                                if (formatted !== v) updateArrayField('Phone', index, formatted);
                                            }}
                                            className="flex-1 min-w-0 px-2 py-2 border border-gray-300 rounded-md text-sm"
                                            placeholder="Phone number"
                                        />
                                        {formData.Phone.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeArrayField('Phone', index)}
                                                className="px-2 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addArrayField('Phone')}
                                    className="mt-1 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                >
                                    + Add Phone
                                </button>
                            </div>

                            {/* WhatsApp */}
                            <div className="min-w-0 overflow-hidden">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    WhatsApp
                                </label>
                                {formData.Whatsapp.map((whatsapp, index) => (
                                    <div key={index} className="flex gap-1 mb-2 min-w-0">
                                        <input
                                            type="tel"
                                            value={whatsapp}
                                            onChange={(e) => updateArrayField('Whatsapp', index, e.target.value)}
                                            onBlur={(e) => {
                                                const v = e.target.value;
                                                const formatted = formatPhone(v);
                                                if (formatted !== v) updateArrayField('Whatsapp', index, formatted);
                                            }}
                                            className="flex-1 min-w-0 px-2 py-2 border border-gray-300 rounded-md text-sm"
                                            placeholder="WhatsApp number"
                                        />
                                        {formData.Whatsapp.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeArrayField('Whatsapp', index)}
                                                className="px-2 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addArrayField('Whatsapp')}
                                    className="mt-1 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                >
                                    + Add WhatsApp
                                </button>
                            </div>

                            {/* Telegram */}
                            <div className="min-w-0 overflow-hidden">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Telegram
                                </label>
                                {formData.Telegram.map((tg, index) => (
                                    <div key={index} className="flex gap-1 mb-2 items-center min-w-0">
                                        <input
                                            type="text"
                                            value={tg?.username ?? ''}
                                            onChange={(e) => updateTelegramField(index, 'username', e.target.value)}
                                            className="flex-1 min-w-0 px-2 py-2 border border-gray-300 rounded-md text-sm"
                                            placeholder="Username"
                                        />
                                        <input
                                            type="text"
                                            value={tg?.tgNumber ?? ''}
                                            onChange={(e) => updateTelegramField(index, 'tgNumber', e.target.value)}
                                            onBlur={(e) => {
                                                const v = e.target.value;
                                                const formatted = formatTgNumber(v);
                                                if (formatted !== v) updateTelegramField(index, 'tgNumber', formatted);
                                            }}
                                            className="flex-1 min-w-0 px-2 py-2 border border-gray-300 rounded-md text-sm"
                                            placeholder="TG Number"
                                        />
                                        {formData.Telegram.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeArrayField('Telegram', index)}
                                                className="px-2 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addArrayField('Telegram')}
                                    className="mt-1 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                >
                                    + Add Telegram
                                </button>
                            </div>
                        </div>

                        {/* Row 3: Address */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Address
                            </label>
                            {formData.Address.map((address, index) => (
                                <div key={index} className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={address}
                                        onChange={(e) => updateArrayField('Address', index, formatAddress(e.target.value))}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="Address"
                                    />
                                    {formData.Address.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeArrayField('Address', index)}
                                            className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => addArrayField('Address')}
                                className="mt-2 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            >
                                + Add Address
                            </button>
                        </div>

                        {/* Row 4: Height, Weight, Waist, Wrist, Shoe Size, Ring Size */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Height (cm)
                                </label>
                                <input
                                    type="number"
                                    step="1"
                                    value={formData.Height}
                                    onChange={(e) => setFormData({ ...formData, Height: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Weight (kg)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.Weight}
                                    onChange={(e) => setFormData({ ...formData, Weight: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Waist (cm)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.waist}
                                    onChange={(e) => setFormData({ ...formData, waist: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Wrist (cm)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.wrist}
                                    onChange={(e) => setFormData({ ...formData, wrist: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Shoe Size
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.shoeSize}
                                    onChange={(e) => setFormData({ ...formData, shoeSize: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Ring Size
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.ringSize}
                                    onChange={(e) => setFormData({ ...formData, ringSize: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        </div>

                        {/* Row 5: Remarks */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Remarks
                            </label>
                            <textarea
                                rows="3"
                                value={formData.remarks}
                                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="Optional remarks..."
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            disabled={loading}
                        >
                            {loading ? 'Updating...' : 'Update'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditFOCustomerModal;
